import {api} from "../../api/api";
import {PayloadAction} from "@reduxjs/toolkit";
import {navigate} from "@reach/router";
import {reviewActions, SubmitPayload} from "../review.slice";
import {select, call, put} from 'redux-saga/effects';
import {RootState} from "../../store/rootReducer";
import {cardsActions} from "../../cards/cards.slice";
import {MultiCardSessionResult, SubcardReviewResult} from "../../common/api";
import {calculateTotalTime} from "../utils/multicard.utils";
import {MULTICARD_DEFAULT_DIFFICULTY} from "../constants/multicard.constants";

interface ReviewCardResponse {
    error?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    card?: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* submitReviewSaga(action: PayloadAction<SubmitPayload>): Generator<any, void, any> {
    const cardId: string = yield select((state: RootState) => state.review.cardId);
    const reviewState: RootState['review'] = yield select((state: RootState) => state.review);
    
    // Defensive: these arrays may be absent on stale state (e.g. dev HMR).
    const subcardTimings = reviewState.subcardTimings ?? [];
    const subcardCorrectness = reviewState.subcardCorrectness ?? [];

    // The subcard shown when "Finish & rate card" was pressed is left ungraded;
    // grade it by the whole-card rating (Again => wrong, Good/Hard/Easy => right).
    const finishCorrect = action.payload.difficulty !== 'again';

    try {
        // Check if this is a multicard submission
        if (reviewState.isMultiCard && reviewState.selectedSubcards.length > 0) {
            // Create a complete session result for the multicard
            const subcardResults: SubcardReviewResult[] = reviewState.selectedSubcards.map((subcard, index) => {
                return {
                    subcardIndex: index,
                    subcard,
                    timeSpent: subcardTimings[index] || 0,
                    isCorrect: subcardCorrectness[index] !== undefined
                        ? subcardCorrectness[index]
                        : finishCorrect,
                    inferred: subcardCorrectness[index] === undefined,
                };
            });

            const multiCardResult: MultiCardSessionResult = {
                cardId,
                totalTime: calculateTotalTime(subcardTimings),
                subcardResults,
                sessionResult: action.payload.difficulty || MULTICARD_DEFAULT_DIFFICULTY,
                completedAt: Date.now()
            };

            // Call the API to submit the multicard review
            yield call([api, 'applyReview'], {
                cardId,
                intervalMinutes: action.payload.intervalMinutes,
                source: 'review',
                isMultiCard: true,
                multiCardResult
            });
        } else {
            // Regular card submission
            yield call([api, 'applyReview'], {
                cardId,
                intervalMinutes: action.payload.intervalMinutes,
                source: 'review',
                difficulty: action.payload.difficulty
            });
        }
    } catch (error) {
        // Never let a failed submit tear down the whole review saga tree.
        // eslint-disable-next-line no-console
        console.error('Failed to apply review:', error);
        return;
    }

    try {
        // Try to get the next card
        const reviewCard = yield call(api.getNextReviewCard) as ReviewCardResponse;

        // Check if there's an error indicating no cards are available
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (reviewCard?.error === 'No cards found') {
            // Set the review state to Completed
            yield put(reviewActions.setCompletedState());
            return;
        }

        // If we have a card, proceed with loading it
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-unsafe-member-access
        if (reviewCard?.card) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            yield put(cardsActions.put([reviewCard.card]));
            yield put(reviewActions.nextCard(reviewCard));
            // Keep the card id in the URL so a refresh resumes the same card.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            navigate(`/review/${reviewCard.card.cardId}`, {replace: true});
        } else {
            // If there's no card in the response, set the state to Completed
            yield put(reviewActions.setCompletedState());
        }
    } catch {
        // If there's an error (like no cards found), set the state to Completed
        yield put(reviewActions.setCompletedState());
    }
}
